/// <amd-module name="Wit/QueriesHub/Common/Components/QueryBreadcrumb" />
import { IObservableArray, IObservableValue, ObservableArray } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dropdown, DropdownCallout, DropdownExpandableButton } from "VSSUI/Dropdown";
import { Icon, IconSize } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { IResizeGroupContext, OverflowButton, ResizeGroup, ResizeGroupContext } from "VSSUI/ResizeGroup";
import { Spinner } from "VSSUI/Spinner";
import { ITableColumn, SimpleTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { Location } from "VSSUI/Utilities/Position";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ITreeItem } from "VSSUI/Utilities/TreeItemProvider";
import { QueryHierarchyItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { IQueryListService, c_myFavoritesGroupKey } from "Wit/Common/QueryListServices/IQueryListService";
import { IQueryService } from "Wit/Common/QueryService/IQueryService";
import { EmptyItem, QueryItem } from "Wit/Common/QueryUtils/Models";
import { getQueryFolderUrl, getQueryUrl } from "Wit/Common/QueryUtils/RoutingUtils";
import { QueryFavoriteButton } from "Wit/QueriesHub/Common/Components/QueryFavoriteButton";
import { QueryUtils } from "Wit/QueriesHub/Common/Utils/QueryUtils";
import * as Resources from "Wit/QueriesHub/Resources";
import * as React from "react";

interface IQueryBreadcrumbProps {
    className?: string;
    query?: IObservableValue<QueryHierarchyItem | undefined>;
    useNewQueryBreadcrumbDesign?: boolean | undefined;
}

export class QueryBreadcrumb extends VssComponent<IQueryBreadcrumbProps> {
    private queryPathItems: string[] = [];
    private breadcrumbItems = new ObservableArray<JSX.Element>();
    private queryListService: IQueryListService;
    private breadcrumbOverflowDropdowns: JSX.Element[] = [];
    private breadcrumbOverflowMenuItems = new ObservableArray<IMenuItem>([]);
    private responsiveIndices = new ObservableArray<number>([]);

    private constructor(props: IQueryBreadcrumbProps, context: IVssComponentContext) {
        super(props, context);
        this.queryListService = this.context.pageContext.getService<IQueryListService>("IQueryListService");
    }

    private initializePathItems = (query: QueryHierarchyItem | undefined, importData: string, isRecycleBin: boolean, customQuery?: boolean) => {
        if (query?.path) {
            this.queryPathItems = query.path.split("/");
        } else if (importData === "csv") {
            this.queryPathItems = [Resources.ImportWorkItems];
        } else if (customQuery) {
            this.queryPathItems = [Resources.CustomQuery];
        } else if (isRecycleBin) {
            this.queryPathItems = [Resources.RecycleBin];
        } else {
            // Set the last item as an empty string to denote this is a new unsaved query.
            this.queryPathItems = [Resources.MyQueries, ""];
        }
    };

    private getFavoriteItems(): IListBoxItem[] {
        const favoriteItems: IListBoxItem[] = [];
        const favoriteGroup = this.queryListService.favoriteGroups.value.find(group => group.id === c_myFavoritesGroupKey);
        if (favoriteGroup) {
            favoriteGroup.items.forEach(favoriteItem => {
                const favoriteItemPath = favoriteItem.path.split("/");
                let favoriteText = "";
                const pathLength = favoriteItemPath.length;
                // only go one folder up when showing the path.
                if (pathLength > 2) {
                    favoriteText = format(Resources.QueryPathTooltipText, favoriteItemPath[pathLength - 2], favoriteItemPath[pathLength - 1]);
                } else {
                    favoriteText = favoriteItem.path;
                }
                favoriteItems.push({
                    id: favoriteItem.id,
                    text: favoriteText,
                    iconProps: { iconName: QueryUtils.getQueryTypeIcon(favoriteItem.queryType) },
                    render: (
                        rowIndex: number,
                        columnIndex: number,
                        tableColumn: ITableColumn<IListBoxItem>,
                        tableItem: IListBoxItem
                    ): JSX.Element => {
                        return (
                            <SimpleTableCell columnIndex={columnIndex} key={rowIndex}>
                                <Icon {...tableItem.iconProps} className={css(tableItem.iconProps?.className, "icon-margin")} />
                                <Tooltip text={favoriteItem.path}>
                                    <span className="flex-grow text-ellipsis">{tableItem.text}</span>
                                </Tooltip>
                                <QueryFavoriteButton
                                    alwaysVisible={true}
                                    item={favoriteItem}
                                    onAddFavorite={(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                                        this.queryListService.addFavorite(favoriteItem);
                                        event.preventDefault();
                                    }}
                                    onRemoveFavorite={(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                                        this.queryListService.removeFavorite(favoriteItem);
                                        event.preventDefault();
                                    }}
                                />
                            </SimpleTableCell>
                        );
                    },
                    tooltipProps: {}
                });
            });
        }

        if (favoriteItems.length > 0) {
            favoriteItems.splice(0, 0, { id: "favorites-header", text: Resources.FavoritesTabName, type: ListBoxItemType.Header });
        }

        return favoriteItems;
    }

    private async initializeBreadcrumbItems(queryId?: string) {
        const allQueriesUrl = getQueryFolderUrl(this.context.pageContext);
        const removeQueriesBreadcrumbDropdowns = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.RemoveQueriesBreadcrumbDropdowns, false)
        const items: JSX.Element[] = [];
        let firstBreadcrumbItem: JSX.Element | undefined = undefined;

        if (removeQueriesBreadcrumbDropdowns) {
            firstBreadcrumbItem = (
                <div className="bolt-button bolt-icon-button disabled subtle">
                    <Icon iconName="ChevronRight" className="query-breadcrumb-chevron padding-horizontal-0 small" />
                </div>
            );
        } else {
            firstBreadcrumbItem = (
                <BreadcrumbDropdown
                    folderName={Resources.QueriesBreadcrumbItem}
                    treeItems={this.queryListService.queryTree.roots}
                    pageContext={this.context.pageContext}
                />
            );
        }
        items.push(
            <div key="query-list-item" className="flex-row">
                <BreadcrumbLink href={allQueriesUrl} folderName={Resources.QueriesBreadcrumbItem} />
                {firstBreadcrumbItem}
            </div>
        );

        let currentPath = "";
        let prevItems = this.queryListService.queryTree.roots;
        let index = 0;

        for (const queryItem of this.queryPathItems) {
            // empty items may exist at the end to denote that there should be no favorite dropdown.
            if (queryItem === "") {
                continue;
            }

            if (index > 0) {
                currentPath += "/";
            }
            currentPath += queryItem;
            let prevItem: ITreeItem<QueryItem | EmptyItem> | undefined;
            if (index === this.queryPathItems.length - 1 && queryId && queryId.length > 0) {
                // compare with queryId so you can find queries which have been renamed
                prevItem = prevItems.find((item: ITreeItem<QueryItem>) => item.data.id === queryId);
            }
            else {
                prevItem = prevItems.find((item: ITreeItem<QueryItem>) => item.data.name === queryItem);
            }

            if (index === this.queryPathItems.length - 1 || queryItem === Resources.ImportWorkItems) {
                // add the favorite dropdown
                const favoriteItems = new ObservableArray(this.getFavoriteItems());

                items.push(
                    <FavoritesDropdown
                        key="favorites-dropdown"
                        favoriteItems={favoriteItems}
                        allQueriesUrl={allQueriesUrl}
                        pageContext={this.context.pageContext}
                            queryName={queryItem}
                            queryTreeItem={prevItem}
                            queryListService={this.queryListService}
                            onFavoriteChanged={() => {
                                favoriteItems.value = this.getFavoriteItems();
                            }}
                    />
                );
            } else {
                const linkHref = getQueryFolderUrl(this.context.pageContext, currentPath);
                const breadcrumbLink = <BreadcrumbLink href={linkHref} folderName={queryItem} />;
                let breadcrumbDropdown = null;

                if (removeQueriesBreadcrumbDropdowns) {
                    if (!!this.queryPathItems[index + 1]) {
                        breadcrumbDropdown = (
                            <div className="bolt-button bolt-icon-button disabled subtle">
                                <Icon iconName="ChevronRight" className="query-breadcrumb-chevron padding-horizontal-0 small" />
                            </div>
                        );
                    }
                } else {
                    if (prevItem && !!this.queryPathItems[index + 1]) {
                        const itemWithChildren = await this.queryListService.getQueryChildren(prevItem, true);
                        const childItems = itemWithChildren ? itemWithChildren.childItems || [] : [];
                        breadcrumbDropdown = (
                            <BreadcrumbDropdown folderName={prevItem.text} treeItems={childItems} pageContext={this.context.pageContext} />
                        );
                        prevItems = childItems;

                        if (this.props.useNewQueryBreadcrumbDesign) {
                            if (this.breadcrumbOverflowDropdowns.length === 0) {
                                this.breadcrumbOverflowDropdowns.push(firstBreadcrumbItem);
                                this.breadcrumbOverflowMenuItems.push({
                                    id: Resources.QueriesBreadcrumbItem + "0",
                                    text: Resources.QueriesBreadcrumbItem,
                                    href: allQueriesUrl
                                });
                            }
                            this.breadcrumbOverflowDropdowns.push(breadcrumbDropdown);
                            this.breadcrumbOverflowMenuItems.push({ id: queryItem + index, text: queryItem, href: linkHref });
                        } else if (index > 0) {
                            this.breadcrumbOverflowDropdowns.push(breadcrumbDropdown);
                            this.breadcrumbOverflowMenuItems.push({ id: queryItem + index, text: queryItem, href: linkHref });
                        }
                    }
                }

                items.push(
                    <div key={index} className="flex-row">
                        {breadcrumbLink}
                        {breadcrumbDropdown}
                    </div>
                );
            }
            index++;
        }

        const overflowButtonInsertIndex = this.props.useNewQueryBreadcrumbDesign ? 0 : 2;

        const overflowButton = this.props.useNewQueryBreadcrumbDesign
            ? <OverflowButton tooltipProps={{text: Resources.MoreFolders}}/>
            : <OverflowButton/>

        if (items.length > 2) {
            items.splice(
                overflowButtonInsertIndex,
                0,
                <div key="overflow" className="flex-row">
                    {overflowButton}
                    <ResizeGroupContext.Consumer>
                        {(resizeGroupContext: IResizeGroupContext): JSX.Element => {
                            return (
                                <Observer menuItems={resizeGroupContext.overflowItems}>
                                    {(props: { menuItems: IMenuItem[] | undefined }) =>
                                        props.menuItems && props.menuItems.length > 0
                                        ? this.breadcrumbOverflowDropdowns[props.menuItems.length - 1]
                                        : null
                                    }
                                </Observer>
                            );
                        }}
                    </ResizeGroupContext.Consumer>
                </div>
            );

            // Do not hide the first 2 items, the overflow button, or the last item.
            // If useNewQueryBreadcrumbDesign is ture, then it can hide all of them except the overflow button and favorites dropdown
            const responsiveIndices = [];
            for (let i = overflowButtonInsertIndex+1; i < items.length - 1; i++) {
                responsiveIndices.push(i);
            }
            this.responsiveIndices.value = responsiveIndices;
        }

        this.breadcrumbItems.value = items;
    }


    private onQueryChanged = () => {
        if (this.queryListService.queryTree.roots) {
            const queryService = this.context.pageContext.getService<IQueryService>("IQueryService");
            this.initializePathItems(this.props.query?.value, "", queryService.isRecycleBin);
            this.initializeBreadcrumbItems(this.props.query?.value?.id);
        }

        return false;
    };

    private async initializeQueryBreadcrumb() {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const queryService = this.context.pageContext.getService<IQueryService>("IQueryService");
        const routeValues = historyService.getState();
        if (this.queryListService.queryFolderItem.value) {
            this.queryPathItems = this.queryListService.queryFolderItem.value.path.split("/");
            // Set the last item as an empty string to denote that there should be no favorite dropdown since the last item is a folder.
            this.queryPathItems.push("");
            this.initializeBreadcrumbItems();
        } else if (routeValues["parentId"]) {
            const query = await this.queryListService.getQueryByPath(routeValues["parentId"]);
            if (query) {
                this.queryPathItems = query.path.split("/");
                // Set the last item as an empty string to denote that there should be no favorite dropdown since the last item is a folder.
                this.queryPathItems.push("");
                this.initializeBreadcrumbItems();
            }
        } else {
            if (this.props.query?.value) {
                this.initializePathItems(this.props.query.value, routeValues["importData"], queryService.isRecycleBin);
                this.initializeBreadcrumbItems(this.props.query?.value?.id);
            } else if (queryService.tempQueryId || queryService.urlWiql) {
                this.initializePathItems(undefined, routeValues["importData"], queryService.isRecycleBin, true);
                this.initializeBreadcrumbItems();
            } else {
                const queryId = queryService.getQueryId();
                const query = await this.queryListService.getQueryByPath(queryId);
                this.initializePathItems(query, routeValues["importData"], queryService.isRecycleBin);
                this.initializeBreadcrumbItems(queryId);
            }
        }
    }

    public async componentDidMount() {
        if (!this.queryListService.queryTree.roots?.length) {
            await this.queryListService.getQueriesAsync();
        }

        await this.initializeQueryBreadcrumb();
    }

    public render(): JSX.Element {
        return (
            <Observer
                breadcrumbItems={this.breadcrumbItems}
                overflowItems={this.breadcrumbOverflowMenuItems}
                responsiveIndices={this.responsiveIndices}
                query={{ observableValue: this.props.query, filter: this.onQueryChanged }}
            >
                {(observerProps: { breadcrumbItems: JSX.Element[]; overflowItems: IMenuItem[]; responsiveIndices: number[] }) =>
                    <ScreenSizeObserver>
                        {(screenSizeProps: { screenSize: ScreenSize }) => {
                            const xsmallScreen = screenSizeProps.screenSize === ScreenSize.xsmall;

                            if (xsmallScreen && this.props.useNewQueryBreadcrumbDesign) {
                                return (
                                    this.breadcrumbItems.length ? (
                                        <div className={css("query-header-breadcrumb-items scroll-hidden flex-row", this.props.className)}>
                                            {observerProps.breadcrumbItems.slice(-1)}
                                        </div>
                                    ) : (
                                        <Spinner ariaLabel={Resources.Loading} className="padding-left-8" />
                                    )
                                );
                            }

                            return (
                                this.breadcrumbItems.length ? (
                                    <ResizeGroup
                                        responsiveLayoutProps={{
                                            responsiveChildren: observerProps.responsiveIndices
                                        }}
                                        overflowMenuItems={observerProps.overflowItems}
                                    >
                                        <div className={css("query-header-breadcrumb-items scroll-hidden flex-row", this.props.className)}>
                                            {observerProps.breadcrumbItems}
                                        </div>
                                    </ResizeGroup>
                                ) : (
                                    <Spinner ariaLabel={Resources.Loading} className="padding-left-8" />
                                )
                            );
                        }}
                    </ScreenSizeObserver>

                }
            </Observer>
        );
    }
}

interface IBreadcrumDropdownProps {
    folderName?: string;
    treeItems: ITreeItem<QueryItem | EmptyItem>[];
    pageContext: IVssPageContext;
}

function BreadcrumbDropdown(props: IBreadcrumDropdownProps) {
    const { folderName, pageContext, treeItems } = props;
    const selection = new DropdownSelection();
    selection.lock();
    return (
        <Dropdown
            ariaLabel={format(Resources.BreadcrumbItemPicker, folderName)}
            items={treeItems
                .filter((treeItem: ITreeItem<QueryItem | EmptyItem>) => {
                    return !treeItem.data.isEmptyFolderContext;
                })
                .map((treeItem: ITreeItem<QueryItem>) => ({
                    id: treeItem.data.id,
                    text: treeItem.data.name,
                    iconProps: { iconName: treeItem.data.isFolder ? "FolderHorizontal" : QueryUtils.getQueryTypeIcon(treeItem.data.queryType) },
                    data: treeItem
                }))}
            renderCallout={props => (
                <DropdownCallout
                    {...props}
                    dropdownOrigin={{ horizontal: Location.start, vertical: Location.start }}
                    anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                />
            )}
            renderExpandable={props => (
                <DropdownExpandableButton
                    {...props}
                    subtle={true}
                    iconProps={{ iconName: "ChevronRight", className: "query-breadcrumb-chevron padding-horizontal-0", size: IconSize.small }}
                    hideDropdownIcon={true}
                />
            )}
            width={200}
            onSelect={(event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<ITreeItem<QueryItem>>) => {
                let url = "";
                if (item?.data?.data.isFolder) {
                    url = getQueryFolderUrl(pageContext, item.data!.data.path);
                } else {
                    url = getQueryUrl(pageContext, item.data!.data.id);
                }
                onClickFPS(pageContext, url, true);
            }}
            selection={selection}
        />
    );
}

interface IFavoritesDropdownProps {
    favoriteItems: IObservableArray<IListBoxItem>;
    allQueriesUrl: string;
    queryName: string;
    pageContext: IVssPageContext;
    queryTreeItem: ITreeItem<QueryItem | EmptyItem> | undefined;
    queryListService: IQueryListService;
    onFavoriteChanged?: () => void;
}

function FavoritesDropdown(props: IFavoritesDropdownProps) {
    const { allQueriesUrl, pageContext, queryListService, queryName, queryTreeItem, onFavoriteChanged } = props;
    const isRecycleBin = pageContext.getService<IQueryService>("IQueryService").isRecycleBin;
    const favoritesSelection = new DropdownSelection();
    favoritesSelection.lock();

    const onAddOrRemoveFavorite = (isAddFavorite: boolean) => {
        if (queryTreeItem) {
            isAddFavorite ?
                queryListService.addFavorite(queryTreeItem.data as QueryItem)
                : queryListService.removeFavorite(queryTreeItem.data as QueryItem);
            onFavoriteChanged?.();
        }
    }

    const overflowDetected = (anchorElement: HTMLElement) => {
        const overflowElement = anchorElement.querySelector<HTMLElement>(".favorite-dropdown-button-text");
        return overflowElement ? overflowElement.scrollWidth > Math.ceil(overflowElement.offsetWidth) : false;
    }

    return (
        <React.Fragment key="favorites-dropdown">
            <Observer items={props.favoriteItems}>
                {(observableProps: { items: IListBoxItem[] }) => {
                    return (
                        <Dropdown
                            items={observableProps.items}
                            selection={favoritesSelection}
                            actions={[{ text: Resources.BrowseAllQueries, iconProps: { iconName: "Home" }, href: allQueriesUrl }]}
                            renderCallout={props => (
                                <DropdownCallout
                                    {...props}
                                    dropdownOrigin={{ horizontal: Location.start, vertical: Location.start }}
                                    anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                                />
                            )}
                            renderExpandable={props => (
                                <DropdownExpandableButton
                                    {...props}
                                    subtle={true}
                                    iconProps={{
                                        iconName: isRecycleBin
                                            ? "Delete"
                                            : queryTreeItem
                                                ? QueryUtils.getQueryTypeIcon((queryTreeItem.data as QueryItem).queryType)
                                                : "ViewList"
                                    }}
                                    tooltipProps={{overflowDetected: overflowDetected}}
                                >
                                    <div className="favorite-dropdown-button-text text-ellipsis body-xl">{queryName}</div>
                                </DropdownExpandableButton>
                            )}
                            width={480}
                            noItemsText={Resources.AddFavoritesMessage}
                            onSelect={(event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem) => {
                                const url = getQueryUrl(pageContext, item.id);
                                onClickFPS(pageContext, url, true);
                            }}
                        />
                    );
                }}
            </Observer>
            {queryTreeItem && (
                <QueryFavoriteButton
                    alwaysVisible={true}
                    item={queryTreeItem.data as QueryItem}
                    onAddFavorite={() => onAddOrRemoveFavorite(true /* isAddFavorite */)}
                    onRemoveFavorite={() => onAddOrRemoveFavorite(false /* isAddFavorite */)}
                />
            )}
        </React.Fragment>
    );
}

interface IBreadcrumbLinkProps {
    href: string;
    folderName: string;
}

function BreadcrumbLink(props: IBreadcrumbLinkProps) {
    return (
        <Link
            className="text-ellipsis query-breadcrumb-item flex-noshrink body-xl"
            subtle={true}
            href={props.href}
            tooltipProps={{ overflowOnly: true }}
            removeUnderline={true}
        >
            {props.folderName}
        </Link>
    );
}
